/* eslint-disable max-len */
import dayjs from 'dayjs';
import { isNil } from 'lodash/fp';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type EventApplicationEntrypointContextDataFragment } from '../../../../api/fragments';
import { useGetEventQuery } from '../../../../api/queries/getEvent';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompanyContext } from '../../../../components/contexts/CompanyContextManager';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import NotFoundResult from '../../../../components/results/NotFoundResult';
import hasPermissions from '../../../../utilities/hasPermissions';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import EventExpired from './EventExpired';

export type EventEntrypointProps = {
    endpoint: EventApplicationEntrypointContextDataFragment;
};

const EventEntrypoint = ({ endpoint }: EventEntrypointProps) => {
    const { eventSlug } = useParams<{ eventSlug: string }>();

    const { id } = endpoint.eventApplicationModule;
    // first load the event
    const { data: eventData, loading: loadingEvent } = useGetEventQuery({
        fetchPolicy: 'network-only',
        variables: {
            urlSlug: eventSlug,
            productionOnly: true,
            eventModuleId: id,
        },
    });

    const translate = useTranslatedString();

    const user = useAccount(true);
    const { availableCompanies } = useCompanyContext();
    const location = useLocation();

    if (loadingEvent) {
        return <PortalLoadingElement />;
    }

    // Event data not found, just throw 404
    if (!eventData || !eventData.event) {
        return <NotFoundResult />;
    }

    const eventName = translate(eventData.event.name);
    const { privateAccess } = eventData.event;
    const companyId = eventData.event.module.company.id;

    if (dayjs().isBefore(eventData.event.period.start)) {
        return <NotFoundResult />;
    }

    // check if event is private
    if (privateAccess) {
        // check if user is logged in
        if (user) {
            // check if user has correct permissions
            const companyMatch = !isNil(
                availableCompanies?.find(({ id: eventCompanyId }) => eventCompanyId === companyId)
            );
            if (companyMatch) {
                // if event is private, active and user has logged and has the correct permissions, check if the event is expired
                if (hasPermissions(eventData.event.permissions, [permissionKind.createApplication])) {
                    return <EventExpired endpoint={endpoint} event={eventData.event} eventName={eventName} />;
                }

                return <NotFoundResult />;
            }

            return <NotFoundResult />;
        }

        return <Navigate state={{ nextPage: location.pathname }} to={{ pathname: '/auth/signIn' }} />;
    }

    return <EventExpired endpoint={endpoint} event={eventData.event} eventName={eventName} />;
};

export default EventEntrypoint;
